/**
 * Production grid — the order × stage lens of the Production surface: every
 * in-flight order (rows) × every stage (columns), each cell the pieces cleared
 * at that station, plus the order's WIP-on-floor. The bird's-eye complement to
 * the "By station" queues on the same surface. Reads `wip.stageBoard`.
 *
 * @see packages/domain/src/garment/wip/compute-stage-board.ts
 */
import { Link } from "@tanstack/react-router";
import type { wip as wipContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";

type Board = wipContract.StageBoardViewWire;
type Row = Board["rows"][number];

export function ProductionGrid() {
  const q = trpc.wip.stageBoard.useQuery();
  const data = q.data;

  if (!data) return <p className="p-6 text-sm text-muted">Loading…</p>;

  if (data.rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-line bg-bg px-4 py-10 text-center">
        <p className="text-sm font-medium text-ink-2">No orders in production</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
          PP-approved orders on the floor appear here as their stations record output.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-paper">
      <table className="w-full min-w-[900px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
            <th className="sticky left-0 z-10 bg-paper px-3 py-2 text-left font-medium">Order</th>
            {data.stages.map((s) => (
              <th key={s.stage} className="px-2 py-2 text-right font-medium">
                {s.label.split(" ")[0]}
              </th>
            ))}
            <th className="px-3 py-2 text-right font-medium">On floor</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <BoardRow key={r.styleId} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BoardRow({ row }: { row: Row }) {
  // The frontier = the last stage with any output; cells past it are "not reached".
  let frontier = -1;
  row.cells.forEach((c, i) => {
    if (c.output > 0) frontier = i;
  });
  return (
    <tr className="border-b border-line last:border-0">
      <td className="sticky left-0 z-10 bg-paper px-3 py-2">
        <Link
          to="/production/styles/$styleId/production"
          params={{ styleId: row.styleId }}
          className="font-medium text-ink hover:text-sky-700"
        >
          {row.styleRef}
        </Link>
        <div className="text-xs text-muted">
          {row.buyerName}
          {row.exFactoryDate ? <span className="ml-1">· ex {fmtDate(row.exFactoryDate)}</span> : null}
        </div>
      </td>
      {row.cells.map((c, i) => (
        <td key={c.stage} className="px-2 py-2 text-right font-mono">
          {c.output > 0 ? (
            <span className={i === frontier ? "font-semibold text-sky-700" : "text-ink-2"}>
              {c.output.toLocaleString("en-IN")}
            </span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
      ))}
      <td className="px-3 py-2 text-right">
        <span className={`font-mono font-semibold ${row.inProgress > 0 ? "text-warn" : "text-muted"}`}>
          {row.inProgress > 0 ? row.inProgress.toLocaleString("en-IN") : "—"}
        </span>
      </td>
    </tr>
  );
}

function fmtDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}
